import { useEffect, useRef, useState } from 'react';

import { Box, Flex, Image } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';

import ImagePlaceholder from 'Asset/image/image-placeholder.svg';
import { AlertOutlineIcon, CopyIcon } from 'Asset/icon';
import EmptyState from 'Component/empty-state';
import { Condition, Iteration } from 'Component/others';
import useGetTalkingPointBrief from 'Domain/productivity-host/useGetTalkingPointBrief';
import useCopyToClipboard from 'Hook/useCopyToClipboard';
import Accordion from 'UiKit/accordion';
import PreviewImage from 'UiKit/preview-image';
import { TextBold, TextMedium, TextRegular } from 'UiKit/typography';

import TalkingPointContentSkeleton from './skeleton';
import styles from './styles.module.css';

const TalkingPointPanel = ({ title }) => {
  return (
    <Box>
      <TextBold className={styles['panel__text--title']}>{title}</TextBold>
    </Box>
  );
};

const TalkingPointChildren = ({ data }) => {
  const containerRef = useRef(null);
  const [previewSrc, setPreviewSrc] = useState(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.querySelectorAll('img').forEach((img) => {
      img.style.cursor = 'pointer';
    });

    const handleClick = (e) => {
      if (e.target.tagName === 'IMG') {
        setPreviewSrc(e.target.src);
      }
    };

    container.addEventListener('click', handleClick);
    return () => container.removeEventListener('click', handleClick);
  }, [data?.content]);

  return (
    <>
      <Box ref={containerRef} dangerouslySetInnerHTML={{ __html: data?.content ?? '' }} />
      <PreviewImage images={previewSrc ? [previewSrc] : []} isOpen={Boolean(previewSrc)} onClose={() => setPreviewSrc(null)} />
    </>
  );
};

const ProductHighlightContent = ({ data }) => {
  const { copyToClipboard } = useCopyToClipboard();

  return (
    <Box className={styles['product-highlight__container']}>
      <TextMedium>
        {data?.startedAt} - {data?.endedAt}
      </TextMedium>
      <Box className={styles['product-highlight__box--list']}>
        <Iteration
          items={data?.items}
          render={(item) => {
            const handleCopy = () => copyToClipboard(item?.sku, 'Nomor SKU');
            return (
              <Box className={styles['product-highlight__box--list-item']}>
                <Box className={styles['product-highlight__box--product']}>
                  <Box className={styles['product-highlight__box--image']}>
                    <Image src={item?.imagePath} fallbackSrc={ImagePlaceholder} className={styles['product-highlight__image']} />
                  </Box>
                  <Box>
                    <TextMedium className={styles['product-highlight__text--name']}>{item?.productName}</TextMedium>
                    <Flex gap={'4px'} alignItems={'center'} onClick={handleCopy}>
                      <TextRegular className={styles['product-highlight__text--sku']}>{item?.sku}</TextRegular>
                      <CopyIcon color="var(--color-primary60)" size="16px" />
                    </Flex>
                  </Box>
                </Box>
                <Condition statement={item?.note}>
                  <Flex alignItems={'center'} gap={'4px'}>
                    <AlertOutlineIcon size="11px" color="var(--color-primary70)" />
                    <TextRegular className={styles['product-highlight__text--note']}>{item?.note}</TextRegular>
                  </Flex>
                </Condition>
              </Box>
            );
          }}
        />
      </Box>
    </Box>
  );
};

const TalkingPointContent = () => {
  const { t } = useTranslation('empty-state');
  const { data, isLoading } = useGetTalkingPointBrief();

  if (isLoading) return <TalkingPointContentSkeleton />;

  return (
    <Box className={styles['content__container']}>
      <Condition
        statement={!isEmpty(data)}
        fallback={
          <Box flex={1}>
            <EmptyState
              title={t('specific_item.title', { itemName: 'Brief' })}
              description={t('specific_item.description', { itemName: 'Brief' })}
              background="var(--color-semantic-white)"
              height="calc(100dvh - 180px)"
            />
          </Box>
        }
      >
        <Accordion header={<TalkingPointPanel title={'Product Highlight'} />} children={<ProductHighlightContent data={data?.productHighlights} />} />
        <Iteration
          items={data?.sections}
          render={(item, index) => {
            return <Accordion header={<TalkingPointPanel title={item?.title} />} children={<TalkingPointChildren data={item} />} key={index} />;
          }}
        />
      </Condition>
    </Box>
  );
};

export default TalkingPointContent;
